/**
 * Axis transform.
 *
 * Applies axis titles (with fieldMapping label fallback), scale type,
 * startAtZero, reverse, grid lines, horizontal bar axis swap, and
 * smart nameGap for rotated labels.
 *
 * ─────────────────────────────────────────────────────────────────────
 * AXIS LABEL SEMANTICS — how showXAxisLabel / showYAxisLabel work
 * across chart modes:
 *
 * ┌────────────────────────────┬─────────────────────┬──────────────────────┐
 * │ Scenario                   │ X axis (bottom)     │ Y axis (left)        │
 * ├────────────────────────────┼─────────────────────┼──────────────────────┤
 * │ Vertical bar/line/area     │ showXAxisLabel →    │ showYAxisLabel →     │
 * │ (single series)            │   X field (category)│   Y field (measure)  │
 * ├────────────────────────────┼─────────────────────┼──────────────────────┤
 * │ Vertical bar/line/area     │ Same                │ showYAxisLabel →     │
 * │ (multi series)             │                     │   y[0] or joined     │
 * │                            │                     │   "A / B / C" for    │
 * │                            │                     │   line/area          │
 * ├────────────────────────────┼─────────────────────┼──────────────────────┤
 * │ Vertical bar stacked /     │ Same                │ Same (stacking only  │
 * │ percent stacked            │                     │   changes formatter) │
 * ├────────────────────────────┼─────────────────────┼──────────────────────┤
 * │ Horizontal bar             │ showYAxisLabel →    │ showXAxisLabel →     │
 * │ (axes swapped)             │   Y field (measure) │   X field (category) │
 * │                            │   (value axis)      │   (category axis)    │
 * ├────────────────────────────┼─────────────────────┼──────────────────────┤
 * │ Heatmap (categoryAxes)     │ showXAxisLabel →    │ showYAxisLabel →     │
 * │                            │   X field           │   yDimension field   │
 * └────────────────────────────┴─────────────────────┴──────────────────────┘
 *
 * Key design decision for horizontal bars:
 *   After the axis swap, the toggles follow the *semantic role* rather
 *   than the visual position:
 *   - `showYAxisLabel` (default true = measure shown) controls the
 *     bottom axis (now the value/measure axis after swap).
 *   - `showXAxisLabel` (default false = category hidden) controls the
 *     left axis (now the category axis after swap).
 *   This ensures the defaults produce the same behavior in both
 *   orientations: measure label visible, category label hidden.
 *
 *   For multi-series horizontal bars, the base builder sets yAxis.name
 *   to undefined (no single measure name), so we fall back to the Y
 *   field mapping to resolve a measure label for the bottom axis.
 * ─────────────────────────────────────────────────────────────────────
 */
import type { EChartsOption } from 'echarts';
import type { FieldMappingMap } from '../../../../../api/types';
import type { ChartStyleState, TypeStyle } from '../../../../edit/visualConfig';
import { getCapabilities } from '../../../../edit/visualConfig';
import { isSingleObject, resolveLabel } from './helpers';
import { maxLabelWidth } from '../utils';

export function applyAxis(
  option: EChartsOption,
  vc: ChartStyleState,
  cs: TypeStyle,
  fieldMappings?: FieldMappingMap | null,
  chartType?: string,
): EChartsOption {
  const result = { ...option };

  // ── X axis ───────────────────────────────────────────────────────────
  if (isSingleObject(result.xAxis)) {
    const xa = result.xAxis as any;

    // Axis title (pre-swap: X = category axis for vertical charts)
    if (!vc.showXAxisLabel) {
      xa.name = '';
    } else if (vc.xAxisTitle) {
      xa.name = vc.xAxisTitle;
    } else {
      xa.name = resolveLabel(fieldMappings, 'x', xa.name ?? '');
    }

    if (vc.reverseAxis) xa.inverse = true;

    // Vertical grid lines
    if (!vc.showGridLines && xa.splitLine) {
      xa.splitLine = { show: false };
    }

    // Smart nameGap: increase when x-axis labels are rotated
    if (xa.axisLabel?.rotate && xa.axisLabel.rotate > 0 && xa.name) {
      xa.nameGap = 50 + Math.round(xa.axisLabel.rotate * 0.5);
    }

    // ── Horizontal bars: swap axes ─────────────────────────────────────
    // In horizontal mode, the X axis becomes the VALUE axis (bottom,
    // shows numbers) and the Y axis becomes the CATEGORY axis (left,
    // shows labels).  We swap data + type, then re-assign names using
    // the correct field mapping for each axis's new semantic role.
    if (cs.horizontal && isSingleObject(result.yAxis)) {
      const ya = result.yAxis as any;

      // Swap data and type
      const xData = xa.data;
      const xType = xa.type;
      xa.data = ya.data;
      xa.type = ya.type || 'value';
      ya.data = xData;
      ya.type = xType || 'category';

      // X axis (bottom) is now the value/measure axis.
      // Use showYAxisLabel (default true) so measure label is shown by default.
      if (!vc.showYAxisLabel) {
        xa.name = '';
      } else if (vc.yAxisTitle) {
        xa.name = vc.yAxisTitle;
      } else {
        // Resolve from Y field mapping (since X is now the value/measure axis)
        xa.name = resolveLabel(fieldMappings, 'y', ya.name ?? '');
      }

      // Y axis (left) is now the category axis.
      // Use showXAxisLabel (default false) so category label is hidden by default.
      if (!vc.showXAxisLabel) {
        ya.name = '';
      } else if (vc.xAxisTitle) {
        ya.name = vc.xAxisTitle;
      } else {
        // Resolve from X field mapping (since Y is now the category axis)
        ya.name = resolveLabel(fieldMappings, 'x', '');
      }

      ya.axisLabel = {
        ...ya.axisLabel,
        formatter: (v: string) => (v.length > 25 ? v.slice(0, 22) + '…' : v),
      };
      // Compute nameGap dynamically from the widest category label
      if (ya.name) {
        const categoryLabels = (ya.data ?? []).map(String);
        const longestLabel = maxLabelWidth(categoryLabels);
        ya.nameGap = longestLabel + 30;
      }
    }
  }

  // ── Y axis ───────────────────────────────────────────────────────────
  if (isSingleObject(result.yAxis)) {
    const ya = result.yAxis as any;
    const caps = getCapabilities((chartType ?? 'bar') as any);

    // For horizontal bars the Y axis name was already resolved in the
    // swap block above — skip re-resolution to avoid overwriting with
    // the wrong field mapping.
    if (cs.horizontal) {
      // Name already set in swap block; nothing to do here.
    } else {
      // Axis title — categoryAxes charts use yDimension label, not y[0]
      if (!vc.showYAxisLabel) {
        ya.name = '';
      } else if (vc.yAxisTitle) {
        ya.name = vc.yAxisTitle;
      } else {
        const yDimLabel = (fieldMappings as any)?.yDimension?.label;
        const resolved = yDimLabel ?? resolveLabel(fieldMappings, 'y', '');
        // Keep the base builder's name if field-mapping resolution returned empty
        if (resolved) ya.name = resolved;

        // Multi-Y measures: join all field labels into one title
        const yFields = (fieldMappings as any)?.y;
        if (Array.isArray(yFields) && yFields.length > 1 && vc.showYAxisLabel) {
          const labels = yFields
            .map((f: any) => f.label ?? f.column ?? '')
            .filter(Boolean);
          ya.name = labels.join(' / ');
        }
      }

      // The base builder (bar.ts / line.ts / scatter.ts) always computes a
      // data-aware nameGap via estimateYAxisNameGap.  We only enforce a
      // minimum floor so the title doesn't clip when tick labels are short.
      if (ya.name) {
        ya.nameGap = Math.max(ya.nameGap ?? 60, 55);
      }
    }

    // Category-axes charts: both axes must stay category — force all labels, no type changes
    if (caps.categoryAxes) {
      ya.axisLabel = { ...ya.axisLabel, interval: 0, overflow: 'truncate', width: 120 };
      // Push axis title away from the (potentially wide) category labels
      if (ya.name) ya.nameGap = 140;
    } else {
      // Log scale conflicts with min=0 (log(0) = -∞), so skip startAtZero
      if (vc.startAtZero && vc.scaleType !== 'log') ya.min = 0;
      if (vc.scaleType === 'log') ya.type = 'log';
      if (vc.reverseAxis) ya.inverse = true;
    }
    if (!vc.showGridLines) ya.splitLine = { show: false };
  }

  return result;
}